import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";
import PageHeader from "../components/PageHeader";

const MODULES = [
    "",
    "users",
    "customers",
    "product_categories",
    "products",
    "orders",
    "rp",
    "packing_lists",
    "load_tags",
];

export default function AuditTrail() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [moduleFilter, setModuleFilter] = useState("");
    const [usernameFilter, setUsernameFilter] = useState("");
    const [error, setError] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (moduleFilter) params.module = moduleFilter;
            if (usernameFilter) params.username = usernameFilter;
            const { data } = await api.get("/audit-logs", { params });
            setLogs(data);
            setError("");
        } catch (err) {
            setError(formatApiError(err?.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    }, [moduleFilter, usernameFilter]);

    useEffect(() => {
        load();
    }, [load]);

    return (
        <div data-testid="audit-trail-page">
            <PageHeader
                title="Audit Trail"
                subtitle="Every field change is tracked with user, timestamp, and previous/new values"
            />

            <div className="bg-white border border-gray-200 rounded-sm p-3 mb-4 flex flex-wrap gap-3 items-end">
                <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                        Module
                    </label>
                    <select
                        value={moduleFilter}
                        onChange={(e) => setModuleFilter(e.target.value)}
                        className="odr-input rounded-sm border border-gray-300 px-3 text-sm bg-white"
                        data-testid="audit-module-filter"
                    >
                        {MODULES.map((m) => (
                            <option key={m} value={m}>
                                {m || "All modules"}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                        Username
                    </label>
                    <input
                        value={usernameFilter}
                        onChange={(e) => setUsernameFilter(e.target.value)}
                        className="odr-input rounded-sm border border-gray-300 px-3 text-sm"
                        placeholder="e.g. operalta"
                        data-testid="audit-user-filter"
                    />
                </div>
                <button
                    onClick={load}
                    className="odr-btn rounded-sm border border-gray-300 px-4 text-sm text-gray-700 hover:bg-gray-50"
                    data-testid="audit-refresh"
                >
                    Refresh
                </button>
            </div>

            {error && (
                <div className="text-sm border border-red-200 bg-red-50 text-red-700 rounded-sm px-3 py-2 mb-4">
                    {error}
                </div>
            )}

            <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto">
                <table className="odr-table" data-testid="audit-table">
                    <thead>
                        <tr>
                            <th>Timestamp</th>
                            <th>User</th>
                            <th>Module</th>
                            <th>Action</th>
                            <th>Record</th>
                            <th>Details</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan={6} className="text-center text-gray-500">
                                    Loading…
                                </td>
                            </tr>
                        )}
                        {!loading && logs.length === 0 && (
                            <tr>
                                <td colSpan={6} className="text-center text-gray-500">
                                    No audit entries yet.
                                </td>
                            </tr>
                        )}
                        {logs.map((l) => (
                            <tr key={l.id} data-testid={`audit-row-${l.id}`}>
                                <td className="odr-mono text-xs whitespace-nowrap">
                                    {new Date(l.timestamp).toLocaleString()}
                                </td>
                                <td className="font-medium">{l.username}</td>
                                <td>
                                    <span className="odr-badge odr-badge-neutral">{l.module}</span>
                                </td>
                                <td className="text-gray-800">{l.action}</td>
                                <td className="odr-mono text-xs text-gray-500">
                                    {(l.record_id || "").slice(0, 8)}
                                </td>
                                <td className="text-gray-700">
                                    <div>{l.description}</div>
                                    {l.field_changes?.length > 0 && (
                                        <ul className="mt-1 space-y-0.5">
                                            {l.field_changes.map((c, idx) => (
                                                <li key={`${l.id}-${c.field}-${idx}`} className="text-xs text-gray-500">
                                                    <span className="font-semibold text-gray-700">
                                                        {c.field}
                                                    </span>{" "}
                                                    <span className="line-through">
                                                        {String(c.old)}
                                                    </span>{" "}
                                                    → <span className="text-gray-900">{String(c.new)}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
